import { Box, Flex } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import {
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
// Internal ATS grid — keeps analytical tables consistent with the rest of the app.
import { Table } from '@/components/ats';
import { ViewMorePopover } from '@/components/ats/ViewMorePopover/ViewMorePopover';
import { KText } from '@/ui-library/components/text';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { fillIdPlaceholders, matchBand, parseMatchPct } from '../../../helpers';
import { useChatSubmit } from '../../../hooks';
import { useAgentForgeStore } from '../../../stores';
import { SuggestedActionChips } from '../SuggestedActionChips';
import type { DataTableBlockProps, DataTableRow } from './DataTableBlock.types';

// Rank columns ("#" / "Rank") duplicate row order, so they're dropped.
const isRankColumn = (header: string): boolean => {
  const h = header.trim().toLowerCase();
  return h === '#' || h === 'rank' || h === 'no.' || h === 'no';
};

const looksLikeEntityID = (id: string | undefined): boolean =>
  !!id && /^\d+$/.test(id);

// Backend `entity` picks the deep-link route; contact profiles are the default.
const entityHref = (
  entity: 'contact' | 'job' | undefined,
  id: string
): string => (entity === 'job' ? `/jobs/${id}` : `/candidates/${id}`);

// Analytical tables (rankings/comparisons/summaries/lists) from present_table.
export const DataTableBlock = ({ data }: DataTableBlockProps) => {
  const {
    intro,
    columns,
    rows,
    ids,
    entity,
    footnote,
    variant,
    suggestedActions,
  } = data;
  const { send, isSending } = useChatSubmit();
  const setMode = useAgentForgeStore((s) => s.setMode);

  const sendFollowup = (template: string) => {
    if (isSending) return;
    void send(fillIdPlaceholders(template, ids ?? []));
  };

  const renderedCols: { header: string; origIdx: number }[] = [];
  columns.forEach((c, i) => {
    if (!isRankColumn(c)) renderedCols.push({ header: c, origIdx: i });
  });

  const tableColumns = renderedCols.map((c, cIdx) => ({
    Header: () => (
      <Text variant="label" as="span" truncate>
        {c.header}
      </Text>
    ),
    accessor: `col${cIdx}`,
    style: { overflow: 'hidden' },
    Cell: (cell: { value?: string; row?: { original?: DataTableRow } }) => {
      const value = cell?.value ?? '';
      const href = cell?.row?.original?._href;
      if (cIdx === 0 && href) {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={href}
                  onClick={() => setMode('floating')}
                  className="block overflow-hidden text-ellipsis whitespace-nowrap text-primary-900 text-sm font-medium hover:underline"
                >
                  {value}
                </Link>
              </TooltipTrigger>
              <TooltipContent style={{ zIndex: 2100 }}>{value}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
      const isSkillCol = c.header.trim().toLowerCase().includes('skill');
      if (isSkillCol && value) {
        const skills = value
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        return (
          <ViewMorePopover
            list={skills}
            type="project"
            renderInitial={(list) =>
              list.map((skill, idx) => (
                <TruncatedTextWithTooltip
                  key={idx}
                  text={skill}
                  maxW="150px"
                  size="sm-medium"
                  toolTipVariant="default"
                  textTransform="capitalize"
                  borderRadius="6px"
                  p="0.125em 0.5em"
                  bgColor="secondary-50"
                />
              ))
            }
            renderOthers={() => (
              <Flex flexDir="column">
                {skills.map((skill, idx) => (
                  <Flex key={idx} alignItems="center" gap=".25em">
                    <KText fontSize="sm" color="secondary-700">
                      {skill}
                    </KText>
                  </Flex>
                ))}
              </Flex>
            )}
          />
        );
      }
      const isMatchCol = c.header.trim().toLowerCase() === 'match';
      const pct = isMatchCol ? parseMatchPct(value) : null;
      if (isMatchCol && pct !== null) {
        const band = matchBand(pct);
        return (
          <Box
            display="inline-flex"
            alignItems="center"
            px="8px"
            py="2px"
            borderRadius="full"
            fontSize="12px"
            fontWeight="600"
            bg={band.light}
            color={band.dark}
            maxW="100%"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            {value}
          </Box>
        );
      }
      return (
        <Text variant="p" as="span" truncate>
          {value || '-'}
        </Text>
      );
    },
  }));

  const tableData: DataTableRow[] = rows.map((row, rIdx) => {
    const id = ids?.[rIdx];
    const obj: DataTableRow = {};
    renderedCols.forEach((c, cIdx) => {
      obj[`col${cIdx}`] = row[c.origIdx] ?? '';
    });
    obj._href = looksLikeEntityID(id)
      ? entityHref(entity, id as string)
      : undefined;
    return obj;
  });

  const variantLabel: Record<NonNullable<typeof variant>, string> = {
    ranking: 'Ranking',
    comparison: 'Comparison',
    list: 'List',
    summary: 'Summary',
  };
  const badge = variant && variant !== 'list' ? variantLabel[variant] : null;

  return (
    <Box data-test-id="agentforge-data-table-block">
      {/* Intro + badge above, footnote + actions below — only the grid is boxed. */}
      {(intro || badge) && (
        <Flex align="center" justify="space-between" gap="10px" mb="8px">
          {intro ? (
            <Text variant="label" as="span" className="text-secondary-900">
              {intro}
            </Text>
          ) : (
            <Box />
          )}
          {badge && (
            <Box
              px="8px"
              py="2px"
              borderRadius="full"
              bg="primary-50"
              color="primary-700"
              fontSize="10px"
              fontWeight="600"
              letterSpacing="0.02em"
              textTransform="uppercase"
              flexShrink={0}
            >
              {badge}
            </Box>
          )}
        </Flex>
      )}

      {/* @ts-expect-error ATS Table is JS with loose prop types. */}
      <Table
        columns={tableColumns}
        data={tableData}
        tableId="agentforge-data-table"
        skipPagination
        layoutStyles={{ border: 'none' }}
        tableStyles={{ width: '100%', tableLayout: 'fixed' }}
      />

      {footnote && (
        <Box mt="8px">
          <Text variant="p" as="span" className="text-secondary-600">
            {footnote}
          </Text>
        </Box>
      )}

      {suggestedActions && suggestedActions.length > 0 && (
        <Box mt="10px">
          <SuggestedActionChips
            actions={suggestedActions}
            onSelect={sendFollowup}
            disabled={isSending}
            testIdPrefix="agentforge-data-table-suggested"
          />
        </Box>
      )}
    </Box>
  );
};
